import { v4 as uuidv4 } from 'uuid';
import { ValidationError } from '../utils/errors.js';

/**
 * Represents an audit log entry for tracking system changes
 */
export class AuditLogEntry {
  /**
   * Create a new AuditLogEntry
   * @param {Object} data - AuditLogEntry configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {string} data.action - Type of action performed
   * @param {string} data.entityType - Type of entity affected
   * @param {string} data.entityId - ID of the affected entity
   * @param {string} data.memberId - ID of member who performed the action
   * @param {Date} [data.timestamp] - When the action occurred (defaults to now)
   * @param {Object} [data.oldValues] - Previous values (for updates)
   * @param {Object} [data.newValues] - New values (for creates/updates)
   * @param {Object} [data.metadata] - Additional context information
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('AuditLogEntry data is required');
    }

    // Validate required fields
    if (!data.action || typeof data.action !== 'string') {
      throw new ValidationError('Action is required', 'action', data.action);
    }

    if (!data.entityType || typeof data.entityType !== 'string') {
      throw new ValidationError('Entity type is required', 'entityType', data.entityType);
    }

    if (!data.entityId || typeof data.entityId !== 'string') {
      throw new ValidationError('Entity ID is required', 'entityId', data.entityId);
    }

    if (!data.memberId || typeof data.memberId !== 'string') {
      throw new ValidationError('Member ID is required', 'memberId', data.memberId);
    }

    // Validate action types
    const validActions = ['create', 'update', 'delete', 'cancel'];
    if (!validActions.includes(data.action)) {
      throw new ValidationError(
        `Invalid action. Must be one of: ${validActions.join(', ')}`,
        'action',
        data.action
      );
    }

    // Validate entity types
    const validEntityTypes = ['booking', 'green', 'rink', 'bookingType', 'timeSlot', 'system'];
    if (!validEntityTypes.includes(data.entityType)) {
      throw new ValidationError(
        `Invalid entity type. Must be one of: ${validEntityTypes.join(', ')}`,
        'entityType',
        data.entityType
      );
    }

    this._id = data._id || uuidv4();
    this.action = data.action;
    this.entityType = data.entityType;
    this.entityId = data.entityId;
    this.memberId = data.memberId;
    this.timestamp = data.timestamp ? new Date(data.timestamp) : new Date();
    this.oldValues = data.oldValues || null;
    this.newValues = data.newValues || null;
    this.metadata = data.metadata || {};

    // Validate timestamp
    if (!(this.timestamp instanceof Date) || isNaN(this.timestamp.getTime())) {
      throw new ValidationError('Invalid timestamp', 'timestamp', data.timestamp);
    }
  }

  /**
   * Get a summary description of this audit entry
   * @returns {string} Human-readable description
   */
  getDescription() {
    const actionMap = {
      create: 'created',
      update: 'updated',
      delete: 'deleted',
      cancel: 'cancelled'
    };

    const actionText = actionMap[this.action] || this.action;
    return `${this.entityType} ${this.entityId} was ${actionText} by ${this.memberId}`;
  }

  /**
   * Check if this entry relates to a specific entity
   * @param {string} entityType - Entity type to check
   * @param {string} entityId - Entity ID to check
   * @returns {boolean} True if entry relates to the entity
   */
  relatesTo(entityType, entityId) {
    return this.entityType === entityType && this.entityId === entityId;
  }

  /**
   * Check if this entry was made by a specific member
   * @param {string} memberId - Member ID to check
   * @returns {boolean} True if entry was made by the member
   */
  madeBy(memberId) {
    return this.memberId === memberId;
  }

  /**
   * Check if this entry occurred within a date range
   * @param {Date} startDate - Start of date range
   * @param {Date} endDate - End of date range
   * @returns {boolean} True if entry occurred within range
   */
  withinDateRange(startDate, endDate) {
    if (!startDate || !endDate) {
      return true; // No range specified
    }

    return this.timestamp >= startDate && this.timestamp <= endDate;
  }

  /**
   * Get the changed fields (for update operations)
   * @returns {Array<string>} Array of field names that changed
   */
  getChangedFields() {
    if (this.action !== 'update' || !this.oldValues || !this.newValues) {
      return [];
    }

    const changedFields = [];
    const allKeys = new Set([
      ...Object.keys(this.oldValues),
      ...Object.keys(this.newValues)
    ]);

    for (const key of allKeys) {
      const oldValue = this.oldValues[key];
      const newValue = this.newValues[key];
      
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changedFields.push(key);
      }
    }

    return changedFields;
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      action: this.action,
      entityType: this.entityType,
      entityId: this.entityId,
      memberId: this.memberId,
      timestamp: this.timestamp.toISOString(),
      oldValues: this.oldValues,
      newValues: this.newValues,
      metadata: this.metadata
    };
    return JSON.stringify(data);
  }

  /**
   * Create AuditLogEntry instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {AuditLogEntry} New AuditLogEntry instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new AuditLogEntry({
      _id: jsonData._id,
      action: jsonData.action,
      entityType: jsonData.entityType,
      entityId: jsonData.entityId,
      memberId: jsonData.memberId,
      timestamp: jsonData.timestamp ? new Date(jsonData.timestamp) : new Date(),
      oldValues: jsonData.oldValues,
      newValues: jsonData.newValues,
      metadata: jsonData.metadata
    });
  }

  /**
   * Create audit log entry for entity creation
   * @param {string} entityType - Type of entity
   * @param {string} entityId - Entity ID
   * @param {string} memberId - Member who created
   * @param {Object} values - Entity values
   * @param {Object} metadata - Additional metadata
   * @returns {AuditLogEntry} New audit log entry
   */
  static createEntry(entityType, entityId, memberId, values, metadata = {}) {
    return new AuditLogEntry({
      action: 'create',
      entityType,
      entityId,
      memberId,
      newValues: values,
      metadata
    });
  }

  /**
   * Create audit log entry for entity update
   * @param {string} entityType - Type of entity
   * @param {string} entityId - Entity ID
   * @param {string} memberId - Member who updated
   * @param {Object} oldValues - Previous values
   * @param {Object} newValues - New values
   * @param {Object} metadata - Additional metadata
   * @returns {AuditLogEntry} New audit log entry
   */
  static updateEntry(entityType, entityId, memberId, oldValues, newValues, metadata = {}) {
    return new AuditLogEntry({
      action: 'update',
      entityType,
      entityId,
      memberId,
      oldValues,
      newValues,
      metadata
    });
  }

  /**
   * Create audit log entry for entity deletion
   * @param {string} entityType - Type of entity
   * @param {string} entityId - Entity ID
   * @param {string} memberId - Member who deleted
   * @param {Object} values - Entity values before deletion
   * @param {Object} metadata - Additional metadata
   * @returns {AuditLogEntry} New audit log entry
   */
  static deleteEntry(entityType, entityId, memberId, values, metadata = {}) {
    return new AuditLogEntry({
      action: 'delete',
      entityType,
      entityId,
      memberId,
      oldValues: values,
      metadata
    });
  }
} 